/**
 * AthleteMessagesInbox — inline "new message from your agent" chip that
 * appears at the very top of the athlete's dashboard.  Auto-polls every
 * 20s so the athlete sees an update without a manual refresh, shows an
 * unread count badge, and deep-links straight into the 1:1 thread.
 *
 * Sits ABOVE the pending agent-invite banner and hero card so a
 * message from an already-approved agent is always the first thing
 * an athlete sees when they open the app.
 */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { MessageCircle, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 20000;

const resolveSrc = (u) =>
  !u ? "" : (u.startsWith("http") || u.startsWith("data:") || u.startsWith("blob:") ? u : `${API}${u}`);

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

export default function AthleteMessagesInbox() {
  const [threads, setThreads] = useState([]);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/api/me/athlete/roster-threads`, { withCredentials: true });
        if (!cancelled) setThreads(r.data?.threads || []);
      } catch {
        if (!cancelled) setThreads([]);
      }
    };
    load();
    timerRef.current = setInterval(load, POLL_MS);
    // Refresh immediately when the tab becomes visible again — feels
    // instant on iOS Safari when returning from the homescreen.
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Only surface threads that have at least ONE unread message from the
  // owner (agent/brand) — anything already read is a no-op card.
  const unreadThreads = threads.filter((t) => (t.unread || 0) > 0);
  if (unreadThreads.length === 0) return null;

  const totalUnread = unreadThreads.reduce((sum, t) => sum + (t.unread || 0), 0);
  // If only one owner is pinging, deep-link straight to that thread;
  // otherwise open the inbox list.
  const single = unreadThreads.length === 1 ? unreadThreads[0] : null;
  const href = single
    ? `/athlete/messages?owner=${single.owner_kind}:${single.owner_id}`
    : "/athlete/messages";

  const accent = single?.accent_color || "#8B5CF6";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 pt-6" data-testid="athlete-messages-inbox">
      <Link
        to={href}
        className="group flex items-center gap-3 rounded-2xl border p-3.5 sm:p-4 transition"
        style={{
          borderColor: `${accent}55`,
          background: `linear-gradient(120deg, ${accent}20 0%, rgba(0,0,0,0.4) 90%)`,
          boxShadow: `0 12px 32px -12px ${accent}55`,
        }}
        data-testid="athlete-messages-inbox-link"
      >
        {/* Owner logo / initial */}
        <div className="relative shrink-0">
          {single?.display_logo ? (
            <img
              src={resolveSrc(single.display_logo)}
              alt=""
              className="w-11 h-11 rounded-full object-cover ring-2"
              style={{ boxShadow: `0 0 0 2px ${accent}80` }}
            />
          ) : (
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center text-base font-black text-black"
              style={{ background: accent }}
            >
              {single ? (single.display_name || "A").charAt(0) : <MessageCircle className="w-5 h-5 text-black" />}
            </div>
          )}
          {/* Unread pill */}
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full px-1 text-[10px] font-black text-black flex items-center justify-center ring-2 ring-black"
            style={{ background: accent }}
            data-testid="athlete-messages-inbox-count"
          >
            {totalUnread > 9 ? "9+" : totalUnread}
          </span>
        </div>

        {/* Copy */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: accent }}>
              {single ? "New message" : "New messages"}
            </p>
            <span className="text-[10px] text-white/40">
              · {fmtWhen(single?.last_message?.created_at)}
            </span>
          </div>
          <p className="text-sm font-bold text-white truncate mt-0.5">
            {single ? single.display_name : `${unreadThreads.length} conversations`}
          </p>
          <p className="text-xs text-white/60 truncate">
            {single?.last_message?.body || "Tap to open your inbox"}
          </p>
        </div>

        <ChevronRight className="w-5 h-5 text-white/40 group-hover:text-white transition shrink-0" />
      </Link>
    </div>
  );
}
